import * as React from "react";
import { Button, Field, Input, Link, Text, Title3, makeStyles } from "@fluentui/react-components";
import { buildApiUrl, redirectToMainPage, saveAuthSession } from "../shared/authService";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f7fb",
    padding: "24px",
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

const AuthPage: React.FC = () => {
  const styles = useStyles();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "submitting">("idle");
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email || !password || status === "submitting") {
      return;
    }

    setStatus("submitting");
    setError(null);

    try {
      const response = await fetch(buildApiUrl("/api/v1/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || "Invalid credentials. Please try again.");
      }

      const data = await response.json();
      if (!data?.access_token) {
        throw new Error("Login failed: missing access token.");
      }

      saveAuthSession(data.access_token, data.user);
      redirectToMainPage();
    } catch (err: any) {
      setError(err?.message || "Unable to sign in right now.");
      setStatus("idle");
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.header}>
          <Title3>Sign In</Title3>
          <Text>Securely connect your account to continue.</Text>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <Field label="Work email" required>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(_event, data) => setEmail(data.value)}
              autoComplete="email"
              required
            />
          </Field>


          <Field label="Password" required>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(_event, data) => setPassword(data.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          {error && <Text role="alert" style={{ color: "#c44536" }}>{error}</Text>}

          <Button appearance="primary" type="submit" disabled={status === "submitting"}>
            {status === "submitting" ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        <div className={styles.footer}>
          <Link href="#">Forgot password?</Link>
          <Text size={200}>Need an account? Contact your admin.</Text>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
